'use client';

import { useContext, useEffect, useMemo, useState } from 'react';
import { getCookie } from 'cookies-next';
import parse from 'html-react-parser';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { COOKIE_KEYS } from '@/constants/constant';
import { useCheckout } from '@/context/checkoutProvider';
import { CreditSummaryContext } from '@/context/creditSummaryProvider';
import { getContentByTitle } from '@/customercenter-ui/components/product/details/helpers/utils/productDataUtils';
import { useAccount } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getCustomerPPMType, getPPMData, PPMPage, FlattenedCustomer } from '@/helpers/utils/ppmHelper';
import { ppmTransform } from '@/helpers/utils/ppmTranfomer';
import { CustomerDetailsPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { RESPONSE_SUCCESS_MESSAGE } from '../../components/customer-detail/constants';

type Props = {
  page: PPMPage;
  orderTotal: number;
  isLoggedIn: boolean;
};

const PPM_CACHE_KEY = 'orderLevelPPMContent';
const PPM_CACHE_HOURS = 6;

const getCustomerDataFromDivisionCustomer = (divisionCustomer: any): FlattenedCustomer => ({
  accountBalance: divisionCustomer?.CreditAccount?.AccountInfo?.accountBalance ?? 0,
  monthlyPaymentAmt: divisionCustomer?.CreditAccount?.AccountInfo?.monthlyPaymentAmt ?? 0,
  creditAccountId: divisionCustomer?.CreditAccount?.creditAccountId ?? '',
  availableCredit: divisionCustomer?.CreditAccount?.AccountInfo?.availableCredit ?? 0,
});

const getPPMMessageKey = (page: string, scenario: string) => `${page}_${scenario}`;

const getCachedPPMEntry = (entryTitle: string) => {
  try {
    const cache = localStorage.getItem(PPM_CACHE_KEY);
    if (!cache) return undefined;

    const parsed = JSON.parse(cache);
    const cachedEntry = parsed?.[entryTitle];

    if (!cachedEntry?.data || !cachedEntry?.date) return undefined;

    const cachedTime = new Date(cachedEntry.date).getTime();
    const currentTime = new Date().getTime();
    const hoursDiff = (currentTime - cachedTime) / (1000 * 60 * 60);

    return hoursDiff <= PPM_CACHE_HOURS ? cachedEntry.data : undefined;
  } catch {
    return undefined;
  }
};

const setCachedPPMEntry = (entryTitle: string, data: any) => {
  try {
    const cache = localStorage.getItem(PPM_CACHE_KEY);
    const parsed = cache ? JSON.parse(cache) : {};

    parsed[entryTitle] = {
      data,
      date: new Date().toISOString(),
    };

    localStorage.setItem(PPM_CACHE_KEY, JSON.stringify(parsed));
  } catch {
    // do nothing
  }
};

const decodeHtml = (html: string) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};

const cleanContentstackHtml = (html: string) => {
  if (!html) return '';

  return decodeHtml(html)
    .replaceAll('<p>', '')
    .replaceAll('</p>', '')
    .replaceAll('&nbsp;', ' ')
    .replaceAll('\r', '')
    .trim();
};

const getHtmlValue = (field: any) => {
  if (!field) return '';

  const value =
    typeof field === 'string'
      ? field
      : field?.rich_text_and_html_editor ?? field?.html_content ?? field?.html ?? field?.value ?? '';

  return cleanContentstackHtml(value);
};

const getPPMContentEntry = (contentData: any) => {
  return contentData?.siteOrder_Level_Ppm_ContentEntries?.[0] ?? contentData?.orderLevel_Ppm_ContentEntries?.[0];
};

const getPPMMessagesFromContent = (contentData: any, page: PPMPage) => {
  const entry = getPPMContentEntry(contentData);

  if (page === 'cart') return entry?.cart_ppm_messages ?? [];
  if (page === 'checkout') return entry?.checkout_ppm_messages ?? [];
  if (page === 'orderConfirmation') return entry?.order_confirmation_ppm_messages ?? [];

  return [];
};

const getTooltipFromContent = (contentData: any) => {
  return getHtmlValue(getPPMContentEntry(contentData)?.tooltip_message?.content);
};

const getPPMEntryTitleByDivision = ({
  ppmContentEntryTitle,
  divisionCode,
}: {
  ppmContentEntryTitle?: string;
  divisionCode?: string;
}) => {
  if (!ppmContentEntryTitle) return '';

  try {
    const parsed = JSON.parse(ppmContentEntryTitle);
    return parsed?.[divisionCode ?? 'ZB'] ?? parsed?.ZB ?? '';
  } catch {
    return ppmContentEntryTitle;
  }
};

export const OrderLevelPPM = ({ page, orderTotal, isLoggedIn }: Props) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const { getCustomerDetails } = useAccount();
  const { customerData, setCustomerData, setCustomerDetailsResponse } = useContext(CreditSummaryContext);
  const { customerDetailsResponse: checkoutCustomerDetails } = useCheckout();

  const [divisionCode, setDivisionCode] = useState('ZB');
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [tooltipContent, setTooltipContent] = useState('');

  const effectiveDivisionCode = divisionCode || customerData?.divisionCode || 'ZB';

  const getCreditDetails = async (data: any) => {
    setIsCustomerLoading(true);

    try {
      const payload = {
        query: {
          customerId: data?.customerId,
          omsCustomerId: data?.omsCustomerId,
          divisionCode: data?.divisionCode,
          idName: data?.idName,
        },
      } as CustomerDetailsPayload;

      const response = await getCustomerDetails(payload);

      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        setCustomerData(response?.Customer);
        setCustomerDetailsResponse(response);
      }
    } catch {
      // do nothing
    } finally {
      setIsCustomerLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;

    if (
      !customerData &&
      checkoutCustomerDetails?.code === RESPONSE_SUCCESS_MESSAGE &&
      checkoutCustomerDetails?.Customer
    ) {
      setCustomerData(checkoutCustomerDetails.Customer);
      setCustomerDetailsResponse(checkoutCustomerDetails);
      return;
    }

    const currentCDPData = getCookie(COOKIE_KEYS.CDP_URL) ?? '';

    if (typeof currentCDPData === 'string' && currentCDPData.length) {
      try {
        const data = JSON.parse(currentCDPData);
        setDivisionCode(data?.divisionCode ?? 'ZB');

        if (!customerData && !checkoutCustomerDetails?.Customer) {
          getCreditDetails(data);
        }
      } catch {
        setIsCustomerLoading(false);
      }
    }
  }, [isLoggedIn, customerData, checkoutCustomerDetails]);

  const ppmCustomer = useMemo(() => {
    if (!isLoggedIn || !customerData) return undefined;

    return getCustomerDataFromDivisionCustomer(customerData?.DivisionCustomers?.[0] ?? {});
  }, [isLoggedIn, customerData]);

  const ppmData = useMemo(() => {
    const customerType = getCustomerPPMType({
      isLoggedIn,
      customer: ppmCustomer,
    });

    return getPPMData({
      customerType,
      customer: ppmCustomer,
      orderTotal,
      projectSettings,
    });
  }, [isLoggedIn, ppmCustomer, orderTotal, projectSettings]);

  const isActiveSOAAccount = () => {
    if (!isLoggedIn) {
      return true;
    }

    const accountInfo = customerData?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo;
    const accountStatus = accountInfo?.accountStatusDisplay?.toLowerCase()?.trim();
    const activeStatus = mapLocaleToMeaningfulFormat().soaAccountCurrentStatus;

    if (accountStatus === activeStatus) {
      return true;
    }

    if (accountStatus) {
      return false;
    }

    const accountBalance = accountInfo?.accountBalance ?? 0;

    return accountBalance <= 0;
  };

  useEffect(() => {
    let activeFetch = true;

    const fetchPPMContent = async () => {
      try {
        const entryTitle = getPPMEntryTitleByDivision({
          ppmContentEntryTitle: projectSettings?.ppmContentEntryTitle,
          divisionCode: effectiveDivisionCode,
        });

        if (!entryTitle || !ppmData?.scenario) {
          setHtmlContent('');
          setTooltipContent('');
          return;
        }

        const ppmMessageKey = getPPMMessageKey(page, ppmData.scenario);

        let contentData: any = getCachedPPMEntry(entryTitle);

        if (!contentData) {
          contentData = await getContentByTitle(entryTitle, false);
          setCachedPPMEntry(entryTitle, contentData);
        }

        const ppmMessages = getPPMMessagesFromContent(contentData, page);
        const matchedMessage = ppmMessages?.find((message: any) => message?.key === ppmMessageKey);

        if (activeFetch) {
          setHtmlContent(getHtmlValue(matchedMessage?.content));
          setTooltipContent(getTooltipFromContent(contentData));
        }
      } catch {
        if (activeFetch) {
          setHtmlContent('');
          setTooltipContent('');
        }
      }
    };

    if (!isCustomerLoading) {
      fetchPPMContent();
    }

    return () => {
      activeFetch = false;
    };
  }, [projectSettings?.ppmContentEntryTitle, page, ppmData?.scenario, isCustomerLoading, effectiveDivisionCode]);

  if (!htmlContent || !ppmData || isCustomerLoading || !isActiveSOAAccount()) return null;

  return (
    <section data-testid="order-level-ppm">
      {parse(htmlContent, {
        replace: ppmTransform({
          currentPPM: ppmData.currentPPM,
          newPPM: ppmData.newPPM,
          increase: ppmData.increase,
          tooltipContent,
          divisionCode: effectiveDivisionCode,
        }),
      })}
    </section>
  );
};
